<script>
import { required } from "vuelidate/lib/validators";
import { layoutMethods, coreEloadDropdownMethods, coreEloadDropdownComputed, coreEloadSKUSchedulingMethods } from '@/state/helpers';
import Swal from 'sweetalert2';
import Multiselect from "vue-multiselect";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from 'moment-timezone';

export default {
  page: {
    title: "E-Load Schedule Form",
  },
  components: {
    Multiselect,
    flatPickr,
  },
  data() {
    return {
      scheduleId: 0,
      selectedPromo: null,
      promoOptions: [],
      providerList: [],
      scheduleData: {
        schedulePromoName: "",
        scheduleSKUCode: "",
        scheduleProvider: "",
        scheduleAmount: "",
        scheduleValidity: "",
        scheduleDescription: "",
        scheduleStartDate: null,
        scheduleEndDate: null,
      },
      originalScheduleData: {
        schedulePromoName: "",
        scheduleSKUCode: "",
        scheduleProvider: "",
        scheduleAmount: "",
        scheduleValidity: "",
        scheduleDescription: "",
        scheduleStartDate: null,
        scheduleEndDate: null,
      },
      baseDateTimeConfig: {
        enableTime: true,
        dateFormat: "Y-m-d h:i K",
        altInput: true,
        altFormat: "F j, Y h:i K",
        time_24hr: false,
      },
      submitted: false,
      scheduleStatus: null,
    };
  },
  validations: {
    scheduleData: {
      scheduleSKUCode: {
        required: function(value) {
          return !this.isPromoSelected || (value !== null && value !== undefined && String(value).trim().length > 0);
        },
        noSpace: function(value) {
          return !this.isPromoSelected || !value || !/\s/.test(String(value));
        }
      },
      scheduleAmount: {
        required: function(value) {
          return !this.isPromoSelected || (value !== null && value !== undefined && String(value).trim().length > 0);
        },
        numericOnly: function(value) {
          return !this.isPromoSelected || !value || /^\d+(\.\d+)?$/.test(String(value).trim());
        }
      },
      scheduleStartDate: {
        required: function(value) {
          return !this.isPromoSelected || !!value;
        }
      },
      scheduleEndDate: {
        required: function(value) {
          return !this.isPromoSelected || !!value;
        }
      }
    },
    selectedPromo: {required}
  },
  computed: {
    ...coreEloadDropdownComputed,
    isPromoSelected() {
      return this.selectedPromo !== null && this.selectedPromo !== undefined;
    },
    isScheduleEditable() {
      return this.scheduleId === 0 || this.scheduleStatus === 'Scheduled';
    },
    canShowSaveButton() {
      // Always show save button in add mode (scheduleId === 0)
      return this.scheduleId === 0 || this.scheduleStatus === 'Scheduled';
    },
    canShowCancelledButton() {
      return this.scheduleId > 0 && this.scheduleStatus === 'Scheduled';
    },
    canShowForceCancelledButton() {
      return this.scheduleId > 0 && this.scheduleStatus === 'Ongoing';
    },
    scheduleStatusText() {
      if (!this.scheduleStatus) return '';
      return this.scheduleStatus;
    },
    scheduleStatusClass() {
      const statusClasses = {
        'Scheduled': 'badge-scheduled',
        'Ongoing': 'badge-ongoing',
        'Completed': 'badge-ended',
        'Ended': 'badge-ended',
        'Cancelled': 'badge-cancelled',
        'Force Cancelled': 'badge-force-cancelled'
      };
      return statusClasses[this.scheduleStatus] || '';
    },
    startDateTimeConfig() {
      const config = {
        ...this.baseDateTimeConfig,
        clickOpens: this.isPromoSelected && this.isScheduleEditable,
        allowInput: this.isPromoSelected && this.isScheduleEditable,
        onChange: this.onStartDateChange,
      };
      
      if (this.scheduleId === 0) {
        // Set minimum date to today at midnight to prevent past dates
        config.minDate = moment().startOf('day').toDate();
        
        // Use minTime to prevent selecting past times on today
        config.minTime = moment().format('HH:mm');
      }
      
      return config;
    },
    endDateTimeConfig() {
      return {
        ...this.baseDateTimeConfig,
        clickOpens: this.isPromoSelected && this.isScheduleEditable,
        allowInput: this.isPromoSelected && this.isScheduleEditable,
        // End date cannot be before start date
        minDate: this.scheduleData.scheduleStartDate || (this.scheduleId === 0 ? 'today' : null),
      };
    },
    isPromoNameChanged() {
      return this.scheduleData.schedulePromoName !== this.originalScheduleData.schedulePromoName;
    },
    isSKUCodeChanged() {
      return this.scheduleData.scheduleSKUCode !== this.originalScheduleData.scheduleSKUCode;
    },
    isProviderChanged() {
      return this.scheduleData.scheduleProvider !== this.originalScheduleData.scheduleProvider;
    },
    isAmountChanged() {
      return this.scheduleData.scheduleAmount !== this.originalScheduleData.scheduleAmount;
    },
    isValidityChanged() {
      return this.scheduleData.scheduleValidity !== this.originalScheduleData.scheduleValidity;
    },
    isDescriptionChanged() {
      const current = this.scheduleData.scheduleDescription || '';
      const original = this.originalScheduleData.scheduleDescription || '';
      return current !== original;
    }
  },
  mounted: async function() {
    const routeId = (this.$route && this.$route.params) ? this.$route.params.id : null;
    const parsedScheduleId = parseInt(routeId, 10);
    this.scheduleId = isNaN(parsedScheduleId) ? 0 : parsedScheduleId;

    this.changeTitle({title: (this.scheduleId > 0 ? "E-Load Schedule View" : "Add Schedule E-Load")});
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: 'Peddlr', to: { name: 'Home'}},
        { text: 'E-Load Core Manager', to: { name: 'E-Load Core SKU Schedule'} },
        { text: 'E-Load Schedule Form' }
      ]
    });

    // Load providers first, then promo options
    await this.getEloadProviderList();
    await this.loadPromoOptions();
    
    // Load schedule data after promo options are loaded (for edit mode)
    if (this.scheduleId > 0) {
      await this.loadScheduleData();
      const title = this.scheduleStatus === 'Scheduled' ? 'Edit E-Load Schedule' : 'E-Load Schedule View';
      this.changeTitle({title: title});
    } else {
      // Reset form for add mode to ensure clean state after refresh
      this.selectedPromo = null;
      this.submitted = false;
      this.scheduleData = {
        schedulePromoName: "",
        scheduleSKUCode: "",
        scheduleProvider: "",
        scheduleAmount: "",
        scheduleValidity: "",
        scheduleDescription: "",
        scheduleStartDate: null,
        scheduleEndDate: null,
      };
      this.originalScheduleData = {
        schedulePromoName: "",
        scheduleSKUCode: "",
        scheduleProvider: "",
        scheduleAmount: "",
        scheduleValidity: "",
        scheduleDescription: "",
        scheduleStartDate: null,
        scheduleEndDate: null,
      };
      // Reset validation
      if (this.$v) {
        this.$v.$reset();
      }
    }
  },
  methods: {
    ...layoutMethods,
    ...coreEloadDropdownMethods,
    ...coreEloadSKUSchedulingMethods,
    async getEloadProviderList() {
      try {
        await this.fetchCoreProvidersList();
        
        if (this.getCoreProvidersList && this.getCoreProvidersList.length > 0) {
          let sortedList = await this.sortOptions('name', [...this.getCoreProvidersList]);
          this.providerList = sortedList.map(provider => ({
            text: provider.name,
            value: provider.id
          }));
        }
      } catch (error) {
        console.error('Error loading provider list:', error);
        this.$alertify.error('Failed to load provider list');
      }
    },
    async sortOptions(propName, arr) {
      arr.sort((a, b) => {
        const nameA = a[`${propName}`].toUpperCase();
        const nameB = b[`${propName}`].toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      return arr;
    },
    onStartDateChange() {
      if (this.scheduleData.scheduleEndDate && this.scheduleData.scheduleStartDate) {
        const startDate = moment(this.scheduleData.scheduleStartDate);
        const endDate = moment(this.scheduleData.scheduleEndDate);
        
        if (endDate.isBefore(startDate)) {
          this.scheduleData.scheduleEndDate = null;
        }
      }
    },
    convertTo24HourFormat(dateTimeString) {
      if (!dateTimeString) return null;
      
      const testMoment = moment(dateTimeString, 'YYYY-MM-DD HH:mm', true);
      if (testMoment.isValid()) {
        return dateTimeString;
      }
      const dateMoment = moment(dateTimeString);
      if (!dateMoment.isValid()) return dateTimeString;
      
      return dateMoment.format('YYYY-MM-DD HH:mm');
    },
    async loadPromoOptions() {
      try {
        await this.fetchCoreListedSKUList();
        
        // Map the SKU list to promo options format
        if (this.getCoreListedSKUList && this.getCoreListedSKUList.length > 0) {
          this.promoOptions = this.getCoreListedSKUList.map(sku => ({
            id: sku.id,
            name: sku.sku_name,
            code: sku.sku_code,
            providerName: sku.providerName || sku.service_provider,
            amount: sku.amount,
            validity: sku.validity,
            description: sku.description
          }));
        }
      } catch (error) {
        console.error('Error loading promo options:', error);
        this.$alertify.error('Failed to load promo options');
      }
    },
    async loadScheduleData() {
      try {
        const response = await this.actionCoreEloadSKUSchedulingAPIPOST({
          params: { id: this.scheduleId },
          action: 'info'
        });

        if (response.status === 'ok' && response.message) {
          const data = response.message;
          
          // Set the schedule status
          this.scheduleStatus = data.status || 'Scheduled';
          
          // Find and set the selected promo from the loaded promo options
          const matchedPromo = this.promoOptions.find(promo => promo.id === data.sku_id);
          
          if (matchedPromo) {
            this.selectedPromo = matchedPromo;
          } else {
            // Fallback: create promo object from original SKU data
            this.selectedPromo = {
              id: data.sku_id,
              sku_name: data.original_sku_name,
              code: data.original_sku_code,
              providerName: data.original_provider_name,
              amount: data.original_amount,
              validity: data.original_validity,
              description: data.original_description
            };
          }

          // Store original SKU values for comparison (from sku table)
          this.originalScheduleData = {
            schedulePromoName: data.original_sku_name,
            scheduleSKUCode: data.original_sku_code,
            scheduleProvider: data.original_provider,
            scheduleAmount: data.original_amount,
            scheduleValidity: data.original_validity,
            scheduleDescription: data.original_description || '',
            scheduleStartDate: data.schedule_start,
            scheduleEndDate: data.schedule_end
          };

          // Populate schedule data with current scheduled values (from ref_sku_scheduling table)
          this.scheduleData = {
            schedulePromoName: data.schedule_sku_name,
            scheduleSKUCode: data.schedule_sku_code,
            scheduleProvider: data.schedule_provider, // Provider ID
            scheduleAmount: data.schedule_amount,
            scheduleValidity: data.schedule_validity,
            scheduleDescription: data.schedule_description || '',
            scheduleStartDate: data.schedule_start,
            scheduleEndDate: data.schedule_end
          };
        }
      } catch (error) {
        console.error('Error loading schedule data:', error);
        this.$alertify.error('Failed to load schedule data');
      }
    },
    async handleSubmit() {
      this.submitted = true;
      this.$v.$touch();
      
      // Check if promo is selected first
      if (!this.selectedPromo) {
        return;
      }
      
      // If promo is selected, validate date fields
      if (this.$v.$invalid && this.scheduleId > 0) {
        return;
      }

      try {
        const params = {
          promoId: this.selectedPromo.id,
          promoName: this.scheduleData.schedulePromoName,
          skuCode: this.scheduleData.scheduleSKUCode,
          provider: this.scheduleData.scheduleProvider,
          amount: this.scheduleData.scheduleAmount,
          validity: this.scheduleData.scheduleValidity,
          description: this.scheduleData.scheduleDescription,
          scheduleStart: this.convertTo24HourFormat(this.scheduleData.scheduleStartDate),
          scheduleEnd: this.convertTo24HourFormat(this.scheduleData.scheduleEndDate)
        };

        let response;
        if (this.scheduleId > 0) {
          // Update existing schedule
          params.id = this.scheduleId;
          response = await this.actionCoreEloadSKUSchedulingAPIPOST({
            params: params,
            action: 'update'
          });
        } else {
          // Create new schedule - check for changes first
          const hasChanges = this.isPromoNameChanged || 
                            this.isSKUCodeChanged || 
                            this.isProviderChanged || 
                            this.isAmountChanged || 
                            this.isValidityChanged || 
                            this.isDescriptionChanged;
          
          if (!hasChanges) {
            // Show "No Changes Found" alert
            Swal.fire({
              title: 'No Changes Found',
              html: 'To create a schedule, you must update at least one detail: Promo Name, SKU Code, Provider, Amount, Validity, or Description.',
              icon: 'warning',
              confirmButtonColor: '#6C5CE7',
              confirmButtonText: 'OK'
            });
            return;
          }

          if (this.$v.$invalid) {
            return;
          }

          
          response = await this.actionCoreEloadSKUSchedulingAPIPOST({
            params: params,
            action: 'create'
          });
        }

        if (response.status === 'ok') {
          if (this.scheduleId > 0) {
            // Update schedule success
            Swal.fire(
              "Success!",
              response.message || "Schedule has been updated successfully.",
              "success"
            ).then(() => {
              this.$router.push({ name: 'E-Load Core SKU Schedule' });
            });
          } else {
            // Create schedule success
            Swal.fire({
              title: 'Promo Schedule Created',
              text: 'Your promo schedule has been successfully created and will take effect on the selected start date.',
              icon: 'success',
              confirmButtonColor: '#6C5CE7',
              confirmButtonText: 'OK'
            }).then(() => {
              this.$router.push({ name: 'E-Load Core SKU Schedule' });
            });
          }
        } else {
          Swal.fire(
            "Error!",
            response.message || "Failed to save schedule.",
            "error"
          );
        }
      } catch (error) {
        console.error('Error saving schedule:', error);
        Swal.fire(
          "Error!",
          "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    },
    onPromoSelect(selectedPromo) {
      if (selectedPromo) {
        // Update the selectedPromo reference
        this.selectedPromo = selectedPromo;
        
        // Find the provider ID by matching the provider name
        let providerId = '';
        if (this.providerList && this.providerList.length > 0) {
          const matchedProvider = this.providerList.find(
            p => p.text.toLowerCase() === selectedPromo.providerName.toLowerCase()
          );
          if (matchedProvider) {
            providerId = matchedProvider.value;
          }
        }

        this.scheduleData.schedulePromoName = selectedPromo.sku_name || selectedPromo.name;
        this.scheduleData.scheduleSKUCode = selectedPromo.code;
        this.scheduleData.scheduleProvider = providerId; // Set the provider ID
        this.scheduleData.scheduleAmount = selectedPromo.amount;
        this.scheduleData.scheduleValidity = selectedPromo.validity;
        this.scheduleData.scheduleDescription = selectedPromo.description || '';
        
        // Reset validation state for date fields when promo is selected
        if (this.$v.scheduleData.scheduleStartDate) {
          this.$v.scheduleData.scheduleStartDate.$reset();
        }
        if (this.$v.scheduleData.scheduleEndDate) {
          this.$v.scheduleData.scheduleEndDate.$reset();
        }
        
        // For new schedules (add mode), set originalScheduleData from selectedPromo to avoid "Changed" labels
        if (this.scheduleId === 0) {
          this.originalScheduleData.schedulePromoName = selectedPromo.sku_name || selectedPromo.name;
          this.originalScheduleData.scheduleSKUCode = selectedPromo.code;
          this.originalScheduleData.scheduleProvider = providerId;
          this.originalScheduleData.scheduleAmount = selectedPromo.amount;
          this.originalScheduleData.scheduleValidity = selectedPromo.validity;
          this.originalScheduleData.scheduleDescription = selectedPromo.description || '';
        }
        // For edit mode, don't update originalScheduleData - this allows change detection to work
      } else {
        this.scheduleData.schedulePromoName = "";
        this.scheduleData.scheduleSKUCode = "";
        this.scheduleData.scheduleProvider = "";
        this.scheduleData.scheduleAmount = "";
        this.scheduleData.scheduleValidity = "";
        this.scheduleData.scheduleDescription = "";
        
        // Clear originalScheduleData when clearing selection in add mode
        if (this.scheduleId === 0) {
          this.originalScheduleData.schedulePromoName = "";
          this.originalScheduleData.scheduleSKUCode = "";
          this.originalScheduleData.scheduleProvider = "";
          this.originalScheduleData.scheduleAmount = "";
          this.originalScheduleData.scheduleValidity = "";
          this.originalScheduleData.scheduleDescription = "";
        }
      }
    },
    async handleCancelSchedule() {
      try {
        const result = await Swal.fire({
          title: 'Cancel Promo Scheduled',
          html: 'Are you sure you want to cancel this scheduled promo?<br><br>This action will remove the schedule and will not apply any changes on the start date.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#6C5CE7',
          cancelButtonColor: '#C12927',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        });

        if (result.isConfirmed) {
          const response = await this.actionCoreEloadSKUSchedulingAPIPOST({
            params: { 
              id: this.scheduleId,
              status: 'cancel'
            },
            action: 'update-status'
          });

          if (response.status === 'ok') {
            Swal.fire({
              title: 'Promo Schedule Cancelled',
              text: 'The scheduled promo has been successfully cancelled.',
              icon: 'success',
              confirmButtonColor: '#6C5CE7',
              confirmButtonText: 'OK'
            }).then(() => {
              this.$router.push({ name: 'E-Load Core SKU Schedule' });
            });
          } else {
            Swal.fire(
              "Error!",
              response.message || "Failed to cancel schedule.",
              "error"
            );
          }
        }
      } catch (error) {
        console.error('Error cancelling schedule:', error);
        Swal.fire(
          "Error!",
          "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    },
    async handleForceCancelSchedule() {
      try {
        const result = await Swal.fire({
          title: 'Force Cancel Schedule?',
          html: 'This schedule is already in progress. If you force cancel it, the promo changes will immediately stop and the SKU will revert to its previous settings.<br><br>Do you want to continue?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#6C5CE7',
          cancelButtonColor: '#C12927',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        });

        if (result.isConfirmed) {
          const response = await this.actionCoreEloadSKUSchedulingAPIPOST({
            params: { 
              id: this.scheduleId,
              status: 'force-cancel'
            },
            action: 'update-status'
          });

          if (response.status === 'ok') {
            Swal.fire({
              title: 'Promo Schedule Cancelled',
              html: 'The ongoing schedule has been successfully canceled.<br><br>The SKU has been reverted to its previous settings.',
              icon: 'success',
              confirmButtonColor: '#6C5CE7',
              confirmButtonText: 'OK'
            }).then(() => {
              this.$router.push({ name: 'E-Load Core SKU Schedule' });
            });
          } else {
            Swal.fire(
              "Error!",
              response.message || "Failed to force cancel schedule.",
              "error"
            );
          }
        }
      } catch (error) {
        console.error('Error force cancelling schedule:', error);
        Swal.fire(
          "Error!",
          "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    next();
  }
};
</script>

<template>
  <div class="row sku-schedule-form">
    <div class="col-lg-8">
      <div class="card">
        <div class="card-body">
          <form class="px-4 py-3" @submit.prevent="handleSubmit()">
            <!-- Promo Name Selection -->
            <div class="row">
              <div class="col-md-12 mb-2">
                <label class="form-label fw-bold">Promo Name<span style="color: red">*</span></label>
                <multiselect
                  v-model="selectedPromo"
                  :options="promoOptions"
                  :multiple="false"
                  placeholder="Select Promo Name"
                  label="name"
                  track-by="id"
                  :preselect-first="false"
                  @input="onPromoSelect"
                  :disabled="scheduleId > 0"
                  :class="{'is-invalid': submitted && $v.selectedPromo.$error}">
                </multiselect>
                <div v-if="submitted && !$v.selectedPromo.required" class="invalid-feedback">
                  Promo Name is required.
                </div>
              </div>
            </div>

            <hr style="border-width: 2px;">

            <!-- Schedule Promo Details Row -->
            <div class="row mt-3">
              <div class="col-md-6 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">Schedule Promo Name</label>
                  <span v-if="isPromoNameChanged" class="d-flex align-items-center fw-semi" style="font-size: 0.75rem;">
                    <box-icon name='edit' size='15px'></box-icon> <span class="ms-1 mt-1" style="font-weight:500;">Changed</span>
                  </span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  v-model="scheduleData.schedulePromoName"
                  placeholder="Promo Name"
                  :disabled="!isPromoSelected || !isScheduleEditable"/>
              </div>

              <div class="col-md-6 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">Schedule SKU Code</label>
                  <span v-if="isSKUCodeChanged" class="d-flex align-items-center fw-semi" style="font-size: 0.75rem;">
                    <box-icon name='edit' size='15px'></box-icon> <span class="ms-1 mt-1" style="font-weight:500;">Changed</span>
                  </span>
                </div>
                <input
                  type="text"
                  :class="['form-control', {'is-invalid': $v.scheduleData.scheduleSKUCode.$dirty && isPromoSelected && $v.scheduleData.scheduleSKUCode.$error}]"
                  v-model="scheduleData.scheduleSKUCode"
                  @blur="$v.scheduleData.scheduleSKUCode.$touch()"
                  placeholder="SKU Code"
                  :disabled="!isPromoSelected || !isScheduleEditable"/>
                <div v-if="$v.scheduleData.scheduleSKUCode.$dirty && isPromoSelected && $v.scheduleData.scheduleSKUCode.$error">
                  <div v-if="!$v.scheduleData.scheduleSKUCode.required" class="invalid-feedback d-block">
                    SKU Code is required.
                  </div>
                  <div v-if="$v.scheduleData.scheduleSKUCode.required && !$v.scheduleData.scheduleSKUCode.noSpace" class="invalid-feedback d-block">
                    SKU Code must not contain spaces.
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule Provider -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">Schedule Provider</label>
                  <span v-if="isProviderChanged" class="d-flex align-items-center fw-semi" style="font-size: 0.75rem;">
                    <box-icon name='edit' size='15px'></box-icon> <span class="ms-1 mt-1" style="font-weight:500;">Changed</span>
                  </span>
                </div>
                <b-form-select
                  v-model="scheduleData.scheduleProvider"
                  :options="providerList"
                  aria-placeholder="Provider"
                  class="form-select shadow-none"
                  :disabled="!isPromoSelected || !isScheduleEditable">
                  <template #first>
                    <b-form-select-option :value="''" disabled>Provider</b-form-select-option>
                  </template>
                </b-form-select>
              </div>
            </div>

            <!-- Schedule Amount and Validity Row -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">Schedule Amount</label>
                  <span v-if="isAmountChanged" class="d-flex align-items-center fw-semi" style="font-size: 0.75rem;">
                    <box-icon name='edit' size='15px'></box-icon> <span class="ms-1 mt-1" style="font-weight:500;">Changed</span>
                  </span>
                </div>
                <input
                  type="text"
                  :class="['form-control', {'is-invalid': $v.scheduleData.scheduleAmount.$dirty && isPromoSelected && $v.scheduleData.scheduleAmount.$error}]"
                  v-model="scheduleData.scheduleAmount"
                  @blur="$v.scheduleData.scheduleAmount.$touch()"
                  placeholder="Amount"
                  :disabled="!isPromoSelected || !isScheduleEditable"/>
                <div v-if="$v.scheduleData.scheduleAmount.$dirty && isPromoSelected && $v.scheduleData.scheduleAmount.$error">
                  <div v-if="!$v.scheduleData.scheduleAmount.required" class="invalid-feedback d-block">
                    Amount is required.
                  </div>
                  <div v-if="$v.scheduleData.scheduleAmount.required && !$v.scheduleData.scheduleAmount.numericOnly" class="invalid-feedback d-block">
                    Amount must contain numbers only.
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">Schedule Validity</label>
                  <span v-if="isValidityChanged" class="d-flex align-items-center fw-semi" style="font-size: 0.75rem;">
                    <box-icon name='edit' size='15px'></box-icon> <span class="ms-1 mt-1" style="font-weight:500;">Changed</span>
                  </span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  v-model="scheduleData.scheduleValidity"
                  placeholder="Validity"
                  :disabled="!isPromoSelected || !isScheduleEditable"/>
              </div>
            </div>

            <!-- Schedule Description -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">Schedule Description</label>
                  <span v-if="isDescriptionChanged" class="d-flex align-items-center fw-semi" style="font-size: 0.75rem;">
                    <box-icon name='edit' size='15px'></box-icon> <span class="ms-1 mt-1" style="font-weight:500;">Changed</span>
                  </span>
                </div>
                <b-form-textarea
                  v-model="scheduleData.scheduleDescription"
                  placeholder="Promo description..."
                  rows="3"
                  max-rows="6"
                  :disabled="!isPromoSelected || !isScheduleEditable">
                </b-form-textarea>
              </div>
            </div>

            <!-- Schedule Start and End Date Time -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold">Schedule Start Date & Time<span style="color: red">*</span></label>
                <div class="position-relative">
                  <flat-pickr
                    :key="`start-${scheduleId}`"
                    v-model="scheduleData.scheduleStartDate"
                    :config="startDateTimeConfig"
                    :class="['form-control flatpickr-with-icon', {'is-invalid': submitted && isPromoSelected && $v.scheduleData.scheduleStartDate.$error}]"
                    placeholder="Start Date"
                    :disabled="!isPromoSelected || !isScheduleEditable">
                  </flat-pickr>
                  <i class="bx bx-calendar flatpickr-icon"></i>
                </div>
                <div v-if="submitted && isPromoSelected && !$v.scheduleData.scheduleStartDate.required" class="invalid-feedback d-block">
                  Start Date is required.
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold">Schedule End Date & Time<span style="color: red">*</span></label>
                <div class="position-relative">
                  <flat-pickr
                    :key="`end-${scheduleId}`"
                    v-model="scheduleData.scheduleEndDate"
                    :config="endDateTimeConfig"
                    :class="['form-control flatpickr-with-icon', {'is-invalid': submitted && isPromoSelected && $v.scheduleData.scheduleEndDate.$error}]"
                    placeholder="End Date"
                    :disabled="!isPromoSelected || !isScheduleEditable">
                  </flat-pickr>
                  <i class="bx bx-calendar flatpickr-icon"></i>
                </div>
                <div v-if="submitted && isPromoSelected && !$v.scheduleData.scheduleEndDate.required" class="invalid-feedback d-block">
                  End Date is required.
                </div>
              </div>
            </div>

            <!-- Status Display -->
            <div v-if="scheduleId > 0 && scheduleStatus" class="row">
              <div class="col-md-12 mb-3">
                <p class="mb-0">
                  <span class="fw-bold">Status: </span> 
                  <span :class="scheduleStatusClass" class="fw-bold">{{ scheduleStatusText }}</span>
                </p>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between align-items-center mt-4">
              <div>
                <!-- Show Cancelled button for Scheduled status -->
                <b-button 
                  v-if="canShowCancelledButton"
                  variant="outline-danger" 
                  class="w-sm" 
                  type="button"
                  @click="handleCancelSchedule">
                    Cancelled
                </b-button>
                
                <!-- Show Force Cancelled button for Ongoing status -->
                <b-button 
                  v-if="canShowForceCancelledButton"
                  variant="outline-danger" 
                  class="w-sm" 
                  type="button"
                  @click="handleForceCancelSchedule">
                    Force Cancelled
                </b-button>
              </div>
              
              <div class="d-flex gap-2">
                <!-- Show Save/Update button only if schedule is editable -->
                <b-button 
                  v-if="canShowSaveButton"
                  variant="primary" 
                  class="w-sm" 
                  type="submit">
                    {{ scheduleId > 0 ? 'Update' : 'Save' }}
                </b-button>
                
                <router-link :to="{ name: 'E-Load Core SKU Schedule', params: {} }" class="tc-white">
                  <b-button variant="light" class="w-sm" type="button">Cancel</b-button>
                </router-link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.sku-schedule-form .disabled-text {
  color: gray;
}

.sku-schedule-form .multiselect.is-invalid .multiselect__tags {
  border-color: #dc3545 !important;
}

.sku-schedule-form .multiselect.is-invalid ~ .invalid-feedback {
  display: block;
}

.sku-schedule-form .multiselect--active .multiselect__tags {
  padding: 8px 40px 8px 8px;
}

.sku-schedule-form .multiselect__tags {
  min-height: 45px;
  display: flex;
  align-items: center;
  padding: 8px;
}

.sku-schedule-form .multiselect--disabled .multiselect__tags {
  background-color: #f5f6f8 !important;
  cursor: not-allowed;
  opacity: 1;
}

.sku-schedule-form .multiselect__single {
  /* background: #804997; */
  background: transparent;
  color: black;
  padding: 2px 5px 2px 5px;
  border-radius: 4px;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  position: relative;
  min-height: auto;
  line-height: 1.4;
  width: fit-content;
  max-width: 100%;
}

.sku-schedule-form .multiselect__tags-wrap {
  display: inline-flex;
  align-items: center;
}

.sku-schedule-form .multiselect__tag-divider {
  display: inline-block;
  width: 1px;
  height: 18px;
  background-color: rgba(255, 255, 255, 0.3);
  margin: 0 0px 0 8px;
}

.sku-schedule-form .multiselect__tag-icon {
  position: relative;
  cursor: pointer;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s ease;
  right: auto;
  top: auto;
  transform: none;
  /* margin-right: 2px; */
}

.sku-schedule-form .multiselect__tag-icon:after {
  content: "x";
  color: white;
  font-size: 20px;
  font-weight: bold;
  line-height: 18px;
}

.sku-schedule-form .multiselect__tag-icon:hover {
  background: transparent;
}

.sku-schedule-form .multiselect__tag-icon:focus {
  outline: none;
}

.sku-schedule-form .multiselect__placeholder {
  color: #6c757d;
  margin-bottom: 0;
  padding-top: 0;
  display: inline-block;
  padding-left: 4px;
}

.sku-schedule-form .multiselect__select {
  right: 8px;
  width: 30px;
  height: 30px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
}

.sku-schedule-form .form-control.flatpickr-input[disabled],
.sku-schedule-form .form-control.flatpickr-input:disabled {
  background-color: #e9ecef !important;
  opacity: 1;
  cursor: not-allowed;
  color: #6c757d;
  pointer-events: none;
}

.sku-schedule-form .flatpickr-input[disabled] ~ .form-control,
.sku-schedule-form .flatpickr-input:disabled ~ .form-control,
.sku-schedule-form .form-control.form-control-input[disabled],
.sku-schedule-form .form-control.form-control-input:disabled,
.sku-schedule-form input.form-control[readonly][disabled] {
  background-color: #e9ecef !important;
  opacity: 1;
  cursor: not-allowed;
  color: #6c757d;
  pointer-events: none;
}

.sku-schedule-form .flatpickr-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #6c757d;
  pointer-events: none;
  z-index: 1;
}

.sku-schedule-form .flatpickr-with-icon {
  padding-left: 40px !important;
}

.sku-schedule-form .flatpickr-with-icon ~ .flatpickr-icon {
  display: block;
}

.sku-schedule-form .badge-scheduled {
  color: #4D56FF;
}

.sku-schedule-form .badge-ongoing {
  color: #508F30;
}

.sku-schedule-form .badge-ended {
  color: #AFAFAF;
}

.sku-schedule-form .badge-cancelled {
  color: #C12928;
}

.sku-schedule-form .badge-force-cancelled {
  color: #C12928;
}

.sku-schedule-form box-icon {
  vertical-align: middle;
  display: inline-flex;
}

.sku-schedule-form .gap-2 {
  gap: 0.5rem;
}
</style>